/**
 * Bookmark Questions Module - BookmarkQuestion Component
 * Practice page for bookmarked questions with instant feedback
 */

import React, { useEffect, useState, useMemo } from "react";
import { Typography, Row, Col, Layout, Button, Card, Progress } from "antd";
import { useHistory } from "react-router-dom";
import { ArrowLeftOutlined } from "@ant-design/icons";

import { Spinner } from "../question_world/components/common";
import {
    MCQ,
    MCA,
    CheckBox,
    Paragraph,
    ShortAns,
} from "../question_world/components/questions";
import { compareArray, getQuestionKey } from "../question_world/helpers";
import { instance } from "../question_world/helpers/api";
import StudentHeader from "../../../Header/student/studentHeader.component";
import SecondaryHeaderComponent from "../../../Header/student/secondaryHeader.component";

import "../question_world/styles/QuickPracticeExam.css";
import "./BookmarkQuestion.css";

const { Header, Content } = Layout;
const { Text, Title } = Typography;

const BookmarkQuestion = () => {
    const [answeredQuestions, setAnsweredQuestions] = useState(new Set());
    const [isLoading, setIsLoading] = useState(false);
    const [userAnswers, setUserAnswers] = useState({});
    const [mode, setMode] = useState("exam"); // 'exam', 'reading'
    const [bookmarks, setBookmarks] = useState([]);

    const history = useHistory();

    const student = useMemo(() => {
        try {
            return JSON.parse(localStorage.getItem("student") || "{}");
        } catch (error) {
            return {};
        }
    }, []);

    const studentId = student?.id || student?._id;

    // Extract questions from bookmarks
    const questions = useMemo(() => {
        return bookmarks
            .map((bookmark) => bookmark.question_id)
            .filter((question) => question != null);
    }, [bookmarks]);

    useEffect(() => {
        localStorage.removeItem("localSavedAnswers");

        window.addEventListener("contextmenu", (event) => event.preventDefault());
        return () => {
            window.removeEventListener("contextmenu", (event) =>
                event.preventDefault()
            );
        };
    }, []);

    useEffect(() => {
        async function fetchBookmarks() {
            if (!studentId) return;
            setIsLoading(true);
            try {
                const response = await instance.get("/student-question-bookmark/questions", {
                    params: {
                        student_id: studentId,
                    },
                });
                const bookmarkData = response?.data?.data || [];
                setBookmarks(bookmarkData);
            } catch (error) {
                console.error("Error fetching bookmarks:", error);
            } finally {
                setIsLoading(false);
            }
        }
        fetchBookmarks();
    }, [studentId]);

    const handleAnswerSelect = (questionKey, answer) => {
        setUserAnswers((prev) => ({
            ...prev,
            [questionKey]: answer,
        }));

        if (!answeredQuestions.has(questionKey)) {
            setAnsweredQuestions((prev) => new Set([...prev, questionKey]));
        }
    };

    const filteredQuestions = useMemo(() => {
        return questions;
    }, [questions]);

    const getProgress = () => {
        if (!filteredQuestions || filteredQuestions.length === 0) return 0;
        let answeredInFiltered = 0;
        filteredQuestions.forEach((question, index) => {
            const questionKey = getQuestionKey(question, index);
            if (answeredQuestions.has(questionKey)) {
                answeredInFiltered++;
            }
        });
        return Math.round((answeredInFiltered / filteredQuestions.length) * 100);
    };

    const getCorrectlyAnsweredCount = () => {
        if (!filteredQuestions || filteredQuestions.length === 0) return 0;

        let correctCount = 0;
        filteredQuestions.forEach((question, index) => {
            const questionKey = getQuestionKey(question, index);
            const userAnswer = userAnswers[questionKey];

            if (!userAnswer || !answeredQuestions.has(questionKey)) {
                return;
            }

            const correctAnswer = question.answer || [];
            const questionType = question.type;

            if (questionType === "MCQ" || questionType === "checkbox") {
                const userAnsArray = Array.isArray(userAnswer)
                    ? userAnswer
                    : [userAnswer];
                const correctAnsArray = Array.isArray(correctAnswer)
                    ? correctAnswer
                    : [correctAnswer];
                if (compareArray(userAnsArray, correctAnsArray)) {
                    correctCount++;
                }
            } else if (questionType === "MCA") {
                const userAnsArray = Array.isArray(userAnswer) ? userAnswer : [];
                const correctAnsArray = Array.isArray(correctAnswer)
                    ? correctAnswer
                    : [];
                if (userAnsArray.length === correctAnsArray.length) {
                    let allCorrect = true;
                    for (let i = 0; i < correctAnsArray.length; i++) {
                        if (userAnsArray[i] !== correctAnsArray[i]) {
                            allCorrect = false;
                            break;
                        }
                    }
                    if (allCorrect) {
                        correctCount++;
                    }
                }
            } else if (questionType === "shortAns" || questionType === "paragraph") {
                const userAnsText = (
                    Array.isArray(userAnswer) ? userAnswer[0] : userAnswer || ""
                )
                    .toString()
                    .trim()
                    .toLowerCase();
                const correctAnsText = (
                    Array.isArray(correctAnswer) ? correctAnswer[0] : correctAnswer || ""
                )
                    .toString()
                    .trim()
                    .toLowerCase();
                if (userAnsText === correctAnsText) {
                    correctCount++;
                }
            }
        });

        return correctCount;
    };

    if (isLoading || !questions) {
        return <Spinner />;
    }

    const totalQuestions = filteredQuestions.length;
    const progress = getProgress();
    const correctlyAnswered = getCorrectlyAnsweredCount();

    const renderQuestion = (question, index) => {
        const questionKey = getQuestionKey(question, index);
        const userAnswer = userAnswers[questionKey];
        const isAnswered = answeredQuestions.has(questionKey);

        const shouldShowAnswer = mode === "reading";
        const showCorrectAnswer = mode === "reading";

        const questionForDisplay = {
            ...question,
            questionId: {
                ...question,
                _id: question._id,
                title: question.title,
                options: question.options || [],
                answer: question.answer || [],
                explanation: question.explanation || "",
                explanationExt: question.explanationExt || [],
                image: question.image || [],
                file: question.file || [],
                optionType: question.optionType,
                type: question.type,
                status: question.status,
            },
            answer: shouldShowAnswer
                ? showCorrectAnswer
                    ? Array.isArray(question.answer)
                        ? question.answer
                        : question.answer
                            ? [question.answer]
                            : []
                    : isAnswered
                        ? Array.isArray(userAnswer)
                            ? userAnswer
                            : userAnswer
                                ? [userAnswer]
                                : []
                        : []
                : [],
            marks: question.point,
        };

        const commonProps = {
            question: questionForDisplay,
            questionIndex: index,
            examId: null,
            groupId: null,
            isResult: shouldShowAnswer,
            examMeta: {
                multipleTimesSubmission: mode === "exam",
                isInstantPractice: true,
            },
            onAnswerSelect:
                mode === "exam"
                    ? (answer) => handleAnswerSelect(questionKey, answer)
                    : undefined,
            isPracticeQuiz: true,
        };

        switch (question.type) {
            case "MCQ":
                return <MCQ {...commonProps} />;
            case "MCA":
                return <MCA {...commonProps} />;
            case "checkbox":
                return <CheckBox {...commonProps} />;
            case "shortAns":
                return <ShortAns {...commonProps} />;
            case "paragraph":
                return <Paragraph {...commonProps} />;
            default:
                return null;
        }
    };

    return (
        <Layout className="quick_practice_exam_layout">
            <Header>
                <StudentHeader />
            </Header>
            <Layout>
                <SecondaryHeaderComponent />
            </Layout>
            <Layout>
                <Content className="custom-container section-padding sec-mh">
                    <div className="quick_practice_exam_header_title">
                        <Title level={6} className="quick_practice_exam_header_title_text" style={{ margin: 0 }}>
                            Bookmarked Questions
                        </Title>
                    </div>

                    <div style={{ padding: "20px 0" }}>
                <Card
                    className="quick_practice_exam_header"
                    style={{
                        
                    }}
                >
                    <Row justify="space-between" className="quick_practice_exam_header_content" align="middle">
                        <Col flex={1} className="text-center">
                            {/* <Text type="secondary" style={{ display: "block", marginTop: 4 }}>
                                {correctlyAnswered} / {totalQuestions} Correct
                            </Text> */}
                            <div style={{ display: "flex", gap: 8 }}>
                                <Button
                                    type={mode === "exam" ? "primary" : "default"}
                                    size="small"
                                    onClick={() => setMode("exam")}
                                >
                                    Exam
                                </Button>
                                <Button
                                    type={mode === "reading" ? "primary" : "default"}
                                    size="small"
                                    onClick={() => setMode("reading")}
                                >
                                    Reading
                                </Button>
                            </div>
                        </Col>
                        <Col flex={1}>
                            <div className="quick_practice_progress_container">
                                <Text strong className="quick_practice_progress_text">
                                    {(() => {
                                        let answeredInFiltered = 0;
                                        filteredQuestions.forEach((question, index) => {
                                            const questionKey = getQuestionKey(question, index);
                                            if (answeredQuestions.has(questionKey)) {
                                                answeredInFiltered++;
                                            }
                                        });
                                        return answeredInFiltered;
                                    })()}{" "}
                                    / {totalQuestions} Answered
                                </Text>

                                <div className="quick_practice_progress_bar_container">
                                    <Progress percent={progress} status="active" showInfo={true} />
                                </div>
                            </div>
                        </Col>
                    </Row>
                </Card>

                <div>
                    {filteredQuestions.length === 0 ? (
                        <Card>
                            <Text type="secondary" style={{ fontSize: 16 }}>
                                No bookmarked questions found.
                            </Text>
                        </Card>
                    ) : (
                        filteredQuestions.map((question, index) => {
                            const questionKey = getQuestionKey(question, index);
                            return (
                                <Card className="quick_practice_exam_question" key={questionKey} style={{ marginBottom: 20 }}>
                                    <Row style={{ marginTop: 0 }}>
                                        <Col span={24}>{renderQuestion(question, index)}</Col>
                                    </Row>
                                </Card>
                            );
                        })
                    )}
                </div>
                    </div>
                </Content>
            </Layout>
        </Layout>
    );
};

export default BookmarkQuestion;
